// An empty view at the default, release builds (D6, APS-508), says so when debug builds did send
// something: a filter that hides data must not look like no data (Mosaic). One cheap read, the
// releases of debug builds over the same app and window, made only for an empty view at the
// default.
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { lastDays, releasesQuery } from '../api/queries'
import { useI18n } from '../i18n'
import type { ScopeSearch } from '../routes/search'

/** Whether debug builds sent sessions in the window while the view, at release builds, is empty. */
export function useDebugOnly(
  projectId: string,
  scope: { app?: string | undefined; days: number },
  empty: boolean,
): boolean {
  const search = useSearch({ strict: false }) as ScopeSearch
  const atDefault = !search.build
  // Minute-floored, so the key is stable across renders.
  const now = Math.floor(Date.now() / 60_000) * 60_000
  const debug = useQuery({
    ...releasesQuery(
      projectId,
      { ...lastDays(scope.days, now), ...(scope.app ? { app_id: scope.app } : {}), build: 'debug' },
      ['debug-only', scope.app ?? 'all', scope.days, now],
    ),
    enabled: empty && atDefault,
  })
  return empty && atDefault && (debug.data ?? []).some((r) => r.sessions > 0)
}

/** The line an empty view adds when only debug builds sent data, with the way to see them. */
export function DebugOnlyLine({
  projectId,
  scope,
  empty,
}: {
  projectId: string
  scope: { app?: string | undefined; days: number }
  empty: boolean
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const show = useDebugOnly(projectId, scope, empty)
  if (!show) return null
  return (
    <span className="debug-only">
      {t('shell.debugOnly')}{' '}
      <button
        type="button"
        className="ul"
        onClick={() =>
          navigate({
            to: '.',
            search: (prev: Record<string, unknown>) => ({ ...prev, build: 'debug' as const }),
          })
        }
      >
        {t('shell.debugOnly.show')}
      </button>
    </span>
  )
}
